import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  FileExcelOutlined,
  Row,
  Table,
  Typography,
  useTranslation,
} from '@lib/index';
import Modal from '@components/molecules/modal';
import { ManualMarkImportErrorRow, ManualMarkImportResultProps } from './types';

const ManualMarkImportResult = ({
  fileName,
  upload,
  visible,
  onClose,
}: ManualMarkImportResultProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { summary } = upload;
  const errors = summary?.errors || [];
  const columns = useMemo(
    () => [
      {
        title: t('workerAccess.manualMark.import.row'),
        dataIndex: 'row',
        width: 80,
        render: (row: number | null) => row
          ?? t('workerAccess.manualMark.import.fileLevel'),
      },
      {
        title: t('workerAccess.manualMark.import.document'),
        dataIndex: 'value',
        width: 180,
      },
      { title: t('workerAccess.manualMark.import.problem'), dataIndex: 'message' },
    ],
    [t],
  );
  const data = errors.map(
    (error, index): ManualMarkImportErrorRow => ({
      ...error,
      key: `${error.row || 'file'}-${error.field}-${index}`,
      value: error.value
        ?? t(`workerAccess.manualMark.import.fields.${error.field}`, {
          defaultValue: error.field,
        }),
      message: t(`workerAccess.manualMark.import.errors.${error.message}`, {
        defaultValue: error.message,
      }),
    }),
  );
  const failed = summary?.failed || 0;
  const created = summary?.created || 0;
  const errorCount = failed || errors.length;
  const hasErrors = upload.importStatus === 'failed' || errorCount > 0;

  return (
    <Modal
      title={(
        <Typography.Text className="text-lg">
          {t('workerAccess.manualMark.import.resultTitle')}
        </Typography.Text>
      )}
      visible={visible}
      onCancel={onClose}
      displayButtons={false}
      width={980}
      footer={(
        <Button type="primary" onClick={onClose}>
          {t('workerAccess.manualMark.import.close')}
        </Button>
      )}
    >
      <Row className="mt-5">
        <Col span={24}>
          <Row className="border border-gray-200 p-4 mb-4" align="middle" gutter={12}>
            <Col>
              <FileExcelOutlined className="text-green-700 text-3xl" />
            </Col>
            <Col flex="auto">
              <Typography.Text strong className="block">
                {fileName}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t('workerAccess.manualMark.import.processedFile')}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={12} className="mb-4">
            {[
              [
                summary?.total || 0,
                t('workerAccess.manualMark.import.processed'),
                'text-blue-600',
              ],
              [created, t('workerAccess.manualMark.import.created'), 'text-green-500'],
              [errorCount, t('workerAccess.manualMark.import.withErrors'), 'text-red-500'],
            ].map(([value, label, color]) => (
              <Col span={8} key={String(label)}>
                <Row className="border border-gray-200 p-5 text-center">
                  <Col span={24}>
                    <Typography.Title level={3} className={`m-0 ${color}`}>
                      {value}
                    </Typography.Title>
                  </Col>
                  <Col span={24}>
                    <Typography.Text>{label}</Typography.Text>
                  </Col>
                </Row>
              </Col>
            ))}
          </Row>
          {hasErrors ? (
            <>
              <Alert
                type="error"
                showIcon
                className="mb-4"
                message={t('workerAccess.manualMark.import.nothingCreated')}
                description={t('workerAccess.manualMark.import.fixAndRetry')}
              />
              <Table
                columns={columns}
                dataSource={data}
                pagination={{
                  current: page,
                  pageSize: 10,
                  total: data.length,
                  onChange: setPage,
                  showTotal: (total) => t('workerAccess.manualMark.import.errorCount', { count: total }),
                }}
                size="small"
              />
            </>
          ) : (
            <Alert
              type="success"
              showIcon
              message={t('workerAccess.manualMark.import.completed', {
                count: created,
              })}
            />
          )}
        </Col>
      </Row>
    </Modal>
  );
};

export default ManualMarkImportResult;
